import type { Microsoft365AdminDto, Microsoft365SettingsBody, Microsoft365SetupBody, Microsoft365TeamAdminDto, Microsoft365TestResultDto } from '@dbrain/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './client.js';

/**
 * Microsoft 365 in Yönetim › Entegrasyonlar (Phase 15e): the firm's setup, its switches, the test, admin consent
 * and removing the setup (organization admins), and the team admins' switch for their own teams. Everything sits
 * under the person's own key (`['microsoft365']`, api/microsoft365.ts), so a change here also refreshes
 * Bağlantılar and the chat. The client secret is write-only: it travels in the setup's request body only, never as
 * a mutation's variables, a query key or an address, and no answer carries it back.
 */
const MICROSOFT365 = ['microsoft365'] as const;
export const microsoft365AdminKey = [...MICROSOFT365, 'admin'] as const;
export const microsoft365TeamsKey = [...MICROSOFT365, 'admin', 'teams'] as const;

const ADMIN = '/api/admin/microsoft365';

/** The answer goes in at once; everything else under the person's key is read again. */
function settle(queryClient: QueryClient, dto?: Microsoft365AdminDto) {
  if (dto) queryClient.setQueryData(microsoft365AdminKey, dto);
  void queryClient.invalidateQueries({
    queryKey: MICROSOFT365,
    // the admin view just set needs no second read
    predicate: (query) => !(dto && query.queryKey.length === microsoft365AdminKey.length && query.queryKey[1] === 'admin'),
  });
}

/** The firm's setup and state (organization admins of a firm; the API answers others 403). */
export function useMicrosoft365Admin(enabled = true) {
  return useQuery({ queryKey: microsoft365AdminKey, queryFn: () => api<Microsoft365AdminDto>('GET', ADMIN), enabled, staleTime: 30_000 });
}

/** A team admin's teams (an organization admin: every team), the firm's state, never the Microsoft settings. */
export function useMicrosoft365Teams(enabled = true) {
  return useQuery({ queryKey: microsoft365TeamsKey, queryFn: () => api<Microsoft365TeamAdminDto>('GET', `${ADMIN}/teams`), enabled, staleTime: 30_000 });
}

/**
 * Step 2 of the setup. `read` builds the body when the request goes out (from the form's state), so the secret
 * is never kept as the mutation's variables; the form clears it after a successful save.
 */
export function useSaveMicrosoft365Setup(read: () => Microsoft365SetupBody) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<Microsoft365AdminDto>('PUT', `${ADMIN}/setup`, read()),
    onSuccess: (dto) => settle(queryClient, dto),
  });
}

/** "Test et": tenant, app and secret at Microsoft, no one's data; the result is kept as the last test. */
export function useMicrosoft365Test() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<Microsoft365TestResultDto>('POST', `${ADMIN}/test`, {}),
    onSettled: () => settle(queryClient),
  });
}

/** The summary's switches and "Kim kullanabilir"; each change is audited on its own. */
export function useMicrosoft365Settings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Microsoft365SettingsBody) => api<Microsoft365AdminDto>('PATCH', ADMIN, body),
    onSuccess: (dto) => settle(queryClient, dto),
  });
}

/** Step 3: Microsoft's admin-consent page for the read permissions (the browser goes there and comes back to the summary). */
export function useMicrosoft365AdminConsent() {
  return useMutation({ mutationFn: () => api<{ url: string }>('POST', `${ADMIN}/admin-consent`, {}) });
}

/** "Kurulumu kaldır": the setup, every team choice and every person's connection (and their tokens) go. */
export function useRemoveMicrosoft365() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('DELETE', ADMIN),
    onSuccess: () => settle(queryClient),
  });
}

/** A team admin's switch for one of their teams, within the firm's limit; the answer is the team admin view. */
export function useMicrosoft365TeamSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, enabled }: { teamId: string; enabled: boolean }) => api<Microsoft365TeamAdminDto>('PUT', `${ADMIN}/teams/${encodeURIComponent(teamId)}`, { enabled }),
    onSuccess: (dto) => {
      queryClient.setQueryData(microsoft365TeamsKey, dto);
      void queryClient.invalidateQueries({ queryKey: MICROSOFT365, predicate: (query) => query.queryKey[2] !== 'teams' });
    },
  });
}
